import React, { useEffect, useState } from 'react';
import { Button, Box, Fade, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MarkDownText from '../MarkDownText/MarkDownText';
import MarkDownEditor from '../MarkDownText/MarkDownEditor';

interface EditableFieldProps {
  label: string;
  value: string;
  onSave: (newValue: string) => void;
  onEditStart?: (field: string) => void;
  onEditCancel?: (field: string) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({ label, value, onSave, onEditStart, onEditCancel }) => {
  const [editMode, setEditMode] = useState(true);
  const [editedValue, setEditedValue] = useState(value);
  const [showEditButton, setShowEditButton] = useState(false);

  const handleSave = () => {
    onSave(editedValue);
    setEditMode(false);
  };

  const handleCancel = () => {
    if (onEditCancel) {
      onEditCancel(editedValue);
    }
    setEditMode(false);
  }

  useEffect(() => {
    setEditedValue(value);
  }, [value]);

  const enteredRef = React.useRef(false);

  useEffect(() => {
    if (editMode && onEditStart && !enteredRef.current) {
      onEditStart(label);
      enteredRef.current = true;
    }
  }, [editMode, onEditStart, label]);

  return (
    <Box onMouseEnter={() => setShowEditButton(true)} onMouseLeave={() => setShowEditButton(false)}>
      {!editMode ? (
        <>
          {showEditButton && (
            <IconButton
              onClick={() => {
                if (onEditStart) onEditStart(label);
                setEditMode(true);
              }}
            >
              <Fade in={showEditButton} timeout={600}>
                <EditIcon fontSize={'medium'} />
              </Fade>
            </IconButton>
          )}
          <Box display="flex" alignItems="center">
            <Box sx={{ maxWidth: '95%', width: '95%', fontFamily: 'Inter', fontSize: '0.8rem' }}>
              <MarkDownText data={value} img={null} />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <MarkDownEditor
            data={editedValue} 
            setEditedValue={setEditedValue} 
          />
          <Box
            m={2}
            display="flex"
            width="100%"
            justifyContent="center"
            alignItems="center">
            <Button
              onClick={handleSave}
              sx={{

                borderRadius: 5,
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#336B87", 
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1FC16B", 
                  boxShadow: "none",
                },
                "&:focus-visible": {
                  outline: "2px solid #F8AD0C",
                  outlineOffset: "2px",
                },
                
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 8,
                  top: 8,
                  bottom: 8,
                  width: 4,
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  transition: "background-color .15s ease",
                },
              }}>
              Guardar
            </Button>
            <Button
              sx={{
                ml: 2,
                borderRadius: 5,
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#336B87",  
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#ff0000ff",  
                  boxShadow: "none",
                },
                "&:focus-visible": {
                  outline: "2px solid #F8AD0C",
                  outlineOffset: "2px",
                },
              
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 8,
                  top: 8,
                  bottom: 8,
                  width: 4,
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  transition: "background-color .15s ease",
                },
              }}
              onClick={handleCancel}>Cancelar</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EditableField;
